import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { PrimaryButton, SecondaryButton, GhostButton } from '../components/buttons';
import { useWizard } from '../context/WizardCtx';
import { saveDraft, loadDraft } from '../utils/draftStorage';
import { CadastroRow } from '../types/wizard';
import { Plus, Trash2, RefreshCcw, Database, AlertCircle, Save } from 'lucide-react';
import { useEstruturaInterna } from '../hooks/useEstruturaInterna';

/**
 * CadastroTableStep props
 */
interface CadastroTableStepProps {
    onNext?: () => void;
}

/**
 * Step 0: Cadastro de Vendedores & Clientes - Edit internal structure data
 */
export const CadastroTableStep: React.FC<CadastroTableStepProps> = ({ onNext }) => {
    const { data, setData } = useWizard();
    const [cadastroRows, setCadastroRows] = useState<CadastroRow[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [rowIdCounter, setRowIdCounter] = useState(1);
    const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});
    const [initialHash, setInitialHash] = useState<string>('');
    const [currentSnapshot, setCurrentSnapshot] = useState<CadastroRow[]>([]);

    // Load original estrutura interna data
    const { isLoading: isLoadingEstrutura, error: estruturaError, rawData } = useEstruturaInterna();

    /**
     * Create hash from cadastro data for change detection
     */
    const createDataHash = (rows: CadastroRow[]): string => {
        return JSON.stringify(rows.map(row => ({
            codigoCliente: row.codigoCliente,
            nomeCliente: row.nomeCliente,
            nomeVendedor: row.nomeVendedor,
            limiteDia: row.limiteDia,
            limiteVolume: row.limiteVolume
        })));
    };

    /**
     * Check if there are real changes compared to initial data
     */
    const isDraft = useMemo(() => {
        const currentHash = createDataHash(cadastroRows);
        return initialHash !== '' && currentHash !== initialHash;
    }, [cadastroRows, initialHash]);

    /**
     * Initialize cadastro data from wizard context, saved draft, or original CSV
     */
    useEffect(() => {
        const initializeData = async () => {
            // Priority 1: Load from saved wizard data (if user navigated back)
            if (data.cadastro && data.cadastro.length > 0) {
                const rows = data.cadastro;
                setCadastroRows(rows);
                setCurrentSnapshot(rows);
                setRowIdCounter(rows.length + 1);
                setInitialHash(createDataHash(rows));
                return;
            }

            // Priority 2: Check for existing draft from previous session
            try {
                const savedDraft = await loadDraft();
                if (savedDraft && savedDraft.cadastro && savedDraft.cadastro.length > 0) {
                    const rows = savedDraft.cadastro;
                    setCadastroRows(rows);
                    setCurrentSnapshot(rows);
                    setRowIdCounter(rows.length + 1);
                    setInitialHash(createDataHash(rows));
                    return;
                }
            } catch (error) {
                console.warn('Failed to load draft:', error);
            }

            // Priority 3: Load from original CSV data
            if (rawData && rawData.length > 0) {
                const rows: CadastroRow[] = rawData.map((row, index) => ({
                    id: `row-${index + 1}-${Date.now()}`,
                    codigoCliente: row.codigoCliente,
                    nomeCliente: row.nomeCliente,
                    nomeVendedor: row.nomeVendedor,
                    limiteDia: row.limiteDia,
                    limiteVolume: row.limiteVolume
                }));
                setCadastroRows(rows);
                setCurrentSnapshot(rows);
                setRowIdCounter(rows.length + 1);
                setInitialHash(createDataHash(rows));
            }
        };

        initializeData();
    }, [data.cadastro, rawData]);

    /**
     * Validate a single row
     */
    const validateRow = (row: CadastroRow): string[] => {
        const errors: string[] = [];

        if (!row.codigoCliente.trim()) {
            errors.push('Código Cliente é obrigatório');
        }

        if (!row.nomeCliente.trim()) {
            errors.push('Nome Cliente é obrigatório');
        }

        if (!row.nomeVendedor.trim()) {
            errors.push('Nome Vendedor é obrigatório');
        }

        // Validate limiteDia
        if (typeof row.limiteDia === 'string' && row.limiteDia.trim()) {
            // String values are allowed (like "Não tem limite...")
        } else if (typeof row.limiteDia === 'number') {
            if (row.limiteDia < 0) {
                errors.push('Limite Dia deve ser ≥ 0');
            }
        } else {
            errors.push('Limite Dia é obrigatório');
        }

        // Validate limiteVolume
        if (typeof row.limiteVolume === 'string' && row.limiteVolume.trim()) {
            // String values are allowed (like "infinito")
        } else if (typeof row.limiteVolume === 'number') {
            if (row.limiteVolume < 0) {
                errors.push('Limite Volume deve ser ≥ 0');
            }
        } else {
            errors.push('Limite Volume é obrigatório');
        }

        return errors;
    };

    /**
     * Validate all rows and check for duplicates
     */
    const validateAllRows = (): Record<string, string[]> => {
        const errors: Record<string, string[]> = {};

        // Validate each row individually
        cadastroRows.forEach(row => {
            const rowErrors = validateRow(row);
            if (rowErrors.length > 0) {
                errors[row.id] = rowErrors;
            }
        });

        // Check for duplicate pairs (Nome Cliente, Código Cliente)
        const pairs = cadastroRows.map(row => `${row.nomeCliente.trim()}|${row.codigoCliente.trim()}`).filter(pair => !pair.startsWith('|') && !pair.endsWith('|'));
        const duplicatePairs = pairs.filter((pair, index) => pairs.indexOf(pair) !== index);

        if (duplicatePairs.length > 0) {
            cadastroRows.forEach(row => {
                const rowPair = `${row.nomeCliente.trim()}|${row.codigoCliente.trim()}`;
                if (duplicatePairs.includes(rowPair)) {
                    if (!errors[row.id]) errors[row.id] = [];
                    errors[row.id].push('Par Nome/Código duplicado');
                }
            });
        }

        return errors;
    };

    /**
     * Update validation errors whenever data changes
     */
    useEffect(() => {
        const errors = validateAllRows();
        setValidationErrors(errors);
    }, [cadastroRows]);

    /**
     * Add a new row
     */
    const addRow = () => {
        const newRow: CadastroRow = {
            id: `row-${rowIdCounter}-${Date.now()}`,
            codigoCliente: '',
            nomeCliente: '',
            nomeVendedor: '',
            limiteDia: '',
            limiteVolume: ''
        };
        setCadastroRows(prev => [...prev, newRow]);
        setRowIdCounter(prev => prev + 1);
    };

    /**
     * Remove a row
     */
    const removeRow = (id: string) => {
        if (cadastroRows.length <= 1) {
            toast.error('Deve haver pelo menos uma linha');
            return;
        }
        setCadastroRows(prev => prev.filter(row => row.id !== id));
    };

    /**
     * Update a field in a row
     */
    const updateRow = (id: string, field: keyof Omit<CadastroRow, 'id'>, value: string | number) => {
        setCadastroRows(prev => prev.map(row => {
            if (row.id === id) {
                let processedValue = value;
                const updatedRow = { ...row };

                // Special handling for limiteDia - calculate limiteVolume automatically
                if (field === 'limiteDia') {
                    if (typeof value === 'string') {
                        // Check if it's a string value like "Sem limite"
                        if (value.trim() && isNaN(parseFloat(value))) {
                            // String value - keep as is and set limiteVolume to "infinito"
                            updatedRow.limiteDia = value;
                            updatedRow.limiteVolume = "infinito";
                        } else {
                            // Try to parse as number
                            const numValue = parseFloat(value);
                            if (!isNaN(numValue) && Number.isInteger(numValue) && numValue > 0) {
                                // Valid positive integer - calculate limiteVolume
                                updatedRow.limiteDia = numValue;
                                updatedRow.limiteVolume = numValue * 47;
                            } else if (value.trim() === '') {
                                // Empty value - reset both fields
                                updatedRow.limiteDia = '';
                                updatedRow.limiteVolume = '';
                            } else {
                                // Invalid number - don't update
                                return row;
                            }
                        }
                    } else if (typeof value === 'number') {
                        if (Number.isInteger(value) && value > 0) {
                            // Valid positive integer
                            updatedRow.limiteDia = value;
                            updatedRow.limiteVolume = value * 47;
                        } else {
                            // Invalid number - don't update
                            return row;
                        }
                    }
                    return updatedRow;
                }

                // For other fields, use existing logic
                if ((field === 'limiteVolume') && typeof value === 'string') {
                    // limiteVolume is now read-only, so ignore updates
                    return row;
                }

                // Try to parse numeric values for other fields
                if (field === 'limiteVolume' && typeof value === 'string') {
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue)) {
                        processedValue = numValue;
                    }
                }

                return { ...row, [field]: processedValue };
            }
            return row;
        }));
    };

    /**
     * Discard changes and reload current snapshot (original CSV or previously saved draft)
     */
    const discardChanges = () => {
        setCadastroRows([...currentSnapshot]);
        setRowIdCounter(currentSnapshot.length + 1);
        toast.success('Mudanças descartadas');
    };

    /**
     * Save current changes as draft
     */
    const handleSaveDraft = async () => {
        const errors = validateAllRows();
        if (Object.keys(errors).length > 0) {
            toast.error('Corrija os erros antes de salvar o rascunho');
            return;
        }

        setIsSavingDraft(true);
        try {
            // Update wizard context with cadastro data
            const updatedData = { ...data, cadastro: cadastroRows, savedStep: 1 as const };
            setData(updatedData);

            // Save draft
            await saveDraft(updatedData);

            // Update current snapshot and initial hash to reflect saved state
            setCurrentSnapshot([...cadastroRows]);
            setInitialHash(createDataHash(cadastroRows));

            toast.success('Rascunho do cadastro salvo!');
        } catch (error) {
            console.error('Error saving draft:', error);
            toast.error('Erro ao salvar rascunho');
        } finally {
            setIsSavingDraft(false);
        }
    };

    /**
     * Save cadastro data and proceed to next step
     */
    const handleSaveAndNext = async () => {
        const errors = validateAllRows();
        if (Object.keys(errors).length > 0) {
            toast.error('Corrija os erros antes de prosseguir');
            return;
        }

        setIsSaving(true);
        try {
            // Update wizard context with cadastro data (DO NOT save draft automatically)
            const updatedData = { ...data, cadastro: cadastroRows };
            setData(updatedData);

            toast.success('Cadastro salvo com sucesso!');

            if (onNext) {
                onNext();
            }
        } catch (error) {
            console.error('Error saving cadastro:', error);
            toast.error('Erro ao salvar cadastro');
        } finally {
            setIsSaving(false);
        }
    };

    /**
     * Format display value for limite fields
     */
    const formatLimiteValue = (value: string | number): string => {
        if (typeof value === 'string') {
            return value;
        }
        return value.toString();
    };

    // Show error if CSV failed to load
    if (estruturaError) {
        return (
            <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
            >
                <div className="text-center mb-6">
                    <h2 className="text-2xl font-display font-bold text-dexco-text-dark mb-2">
                        Cadastro de Vendedores & Clientes
                    </h2>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                        <div className="text-left">
                            <p className="text-red-800 font-medium">Erro ao carregar estrutura interna</p>
                            <p className="text-red-600 text-sm">{estruturaError}</p>
                        </div>
                    </div>
                </div>
            </motion.div>
        );
    }

    const hasValidationErrors = Object.keys(validationErrors).length > 0;

    return (
        <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
        >
            {/* Header */}
            <div className="text-center">
                <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                        <Database className="w-6 h-6 text-white" />
                    </div>
                </div>
                <h2 className="font-display font-semibold text-2xl text-dexco-text-dark mb-2">
                    Cadastro de Vendedores & Clientes
                </h2>
                <p className="text-gray-600 font-body">
                    Gerencie a estrutura interna de vendedores e clientes antes de configurar cotas e limites
                </p>
                {isLoadingEstrutura && (
                    <p className="text-sm text-blue-600 mt-2">Carregando estrutura interna...</p>
                )}
            </div>

            {/* Data Table */}
            <div className="bg-dexco-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Table Header */}
                <div className="hidden md:grid md:grid-cols-12 gap-4 bg-dexco-gray-bg px-4 py-3 border-b border-gray-200">
                    <div className="col-span-2 text-sm font-body font-semibold text-dexco-text-dark">Código Cliente</div>
                    <div className="col-span-3 text-sm font-body font-semibold text-dexco-text-dark">Nome Cliente</div>
                    <div className="col-span-3 text-sm font-body font-semibold text-dexco-text-dark">Nome Vendedor</div>
                    <div className="col-span-2 text-sm font-body font-semibold text-dexco-text-dark">Limite Dia</div>
                    <div className="col-span-1 text-sm font-body font-semibold text-dexco-text-dark">Limite Volume</div>
                    <div className="col-span-1 text-sm font-body font-semibold text-dexco-text-dark">Ações</div>
                </div>

                {/* Table Body */}
                <div className="max-h-96 overflow-y-auto divide-y divide-gray-200">
                    {cadastroRows.map((row, index) => {
                        const rowErrors = validationErrors[row.id] || [];
                        const hasRowErrors = rowErrors.length > 0;

                        return (
                            <motion.div
                                key={row.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.02 }}
                                className={hasRowErrors ? 'bg-red-50' : ''}
                            >
                                {/* Desktop Layout */}
                                <div className="hidden md:grid md:grid-cols-12 gap-4 px-4 py-3 hover:bg-gray-50 items-center">
                                    <div className="col-span-2">
                                        <input
                                            type="text"
                                            value={row.codigoCliente}
                                            onChange={(e) => updateRow(row.id, 'codigoCliente', e.target.value)}
                                            placeholder="COD001"
                                            className={`w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${hasRowErrors ? 'border-red-300' : 'border-gray-300'}`}
                                        />
                                    </div>

                                    <div className="col-span-3">
                                        <input
                                            type="text"
                                            value={row.nomeCliente}
                                            onChange={(e) => updateRow(row.id, 'nomeCliente', e.target.value)}
                                            placeholder="Nome do Cliente"
                                            className={`w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${hasRowErrors ? 'border-red-300' : 'border-gray-300'}`}
                                        />
                                    </div>

                                    <div className="col-span-3">
                                        <input
                                            type="text"
                                            value={row.nomeVendedor}
                                            onChange={(e) => updateRow(row.id, 'nomeVendedor', e.target.value)}
                                            placeholder="Nome do Vendedor"
                                            className={`w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${hasRowErrors ? 'border-red-300' : 'border-gray-300'}`}
                                        />
                                    </div>

                                    <div className="col-span-2">
                                        <input
                                            type="text"
                                            value={formatLimiteValue(row.limiteDia)}
                                            onChange={(e) => updateRow(row.id, 'limiteDia', e.target.value)}
                                            placeholder="5 ou 'Sem limite'"
                                            className={`w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${hasRowErrors ? 'border-red-300' : 'border-gray-300'}`}
                                        />
                                    </div>

                                    <div className="col-span-1">
                                        <input
                                            type="text"
                                            value={formatLimiteValue(row.limiteVolume)}
                                            readOnly
                                            placeholder="Calculado automaticamente"
                                            className={`w-full px-3 py-2 text-sm border rounded bg-gray-50 text-gray-600 cursor-not-allowed ${hasRowErrors ? 'border-red-300' : 'border-gray-300'}`}
                                            title="Valor calculado automaticamente como Limite Diário × 47"
                                        />
                                    </div>

                                    <div className="col-span-1 flex justify-center">
                                        <button
                                            onClick={() => removeRow(row.id)}
                                            className="text-red-500 hover:text-red-700 p-1 rounded transition-colors duration-200"
                                            disabled={cadastroRows.length <= 1}
                                            aria-label="Remover linha"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                                {/* Mobile Layout - Cards */}
                                <div className="md:hidden p-4 space-y-3">
                                    <div className="flex justify-between items-start">
                                        <div className="text-sm font-semibold text-gray-700">
                                            Registro {index + 1}
                                        </div>
                                        <button
                                            onClick={() => removeRow(row.id)}
                                            className="text-red-500 hover:text-red-700 p-1 rounded transition-colors duration-200"
                                            disabled={cadastroRows.length <= 1}
                                            aria-label="Remover linha"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-1 gap-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Código Cliente</label>
                                            <input
                                                type="text"
                                                value={row.codigoCliente}
                                                onChange={(e) => updateRow(row.id, 'codigoCliente', e.target.value)}
                                                placeholder="COD001"
                                                className={`w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${hasRowErrors ? 'border-red-300' : 'border-gray-300'}`}
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Nome Cliente</label>
                                            <input
                                                type="text"
                                                value={row.nomeCliente}
                                                onChange={(e) => updateRow(row.id, 'nomeCliente', e.target.value)}
                                                placeholder="Nome do Cliente"
                                                className={`w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${hasRowErrors ? 'border-red-300' : 'border-gray-300'}`}
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Nome Vendedor</label>
                                            <input
                                                type="text"
                                                value={row.nomeVendedor}
                                                onChange={(e) => updateRow(row.id, 'nomeVendedor', e.target.value)}
                                                placeholder="Nome do Vendedor"
                                                className={`w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${hasRowErrors ? 'border-red-300' : 'border-gray-300'}`}
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">Limite Dia</label>
                                                <input
                                                    type="text"
                                                    value={formatLimiteValue(row.limiteDia)}
                                                    onChange={(e) => updateRow(row.id, 'limiteDia', e.target.value)}
                                                    placeholder="5 ou 'Sem limite'"
                                                    className={`w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${hasRowErrors ? 'border-red-300' : 'border-gray-300'}`}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">Limite Volume</label>
                                                <input
                                                    type="text"
                                                    value={formatLimiteValue(row.limiteVolume)}
                                                    readOnly
                                                    placeholder="Calculado automaticamente"
                                                    className={`w-full px-3 py-2 text-sm border rounded bg-gray-50 text-gray-600 cursor-not-allowed ${hasRowErrors ? 'border-red-300' : 'border-gray-300'}`}
                                                    title="Valor calculado automaticamente como Limite Diário × 47"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Row errors for mobile */}
                                    {hasRowErrors && (
                                        <div className="mt-2 p-2 bg-red-100 border border-red-200 rounded text-sm text-red-700">
                                            <ul className="list-disc list-inside space-y-1">
                                                {rowErrors.map((error, idx) => (
                                                    <li key={idx}>{error}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>

                                {/* Row errors for desktop */}
                                {hasRowErrors && (
                                    <div className="hidden md:block px-4 pb-3">
                                        <div className="text-sm text-red-600">
                                            {rowErrors.join(', ')}
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        );
                    })}
                </div>

                {/* Add row button */}
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                    <GhostButton
                        onClick={addRow}
                        leftIcon={<Plus className="w-4 h-4" />}
                    >
                        Adicionar Linha
                    </GhostButton>
                </div>
            </div>

            {/* Validation Summary */}
            {hasValidationErrors && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-5 h-5 text-red-600" />
                        <h4 className="text-sm font-medium text-red-800">
                            Erros de Validação ({Object.keys(validationErrors).length} registro{Object.keys(validationErrors).length !== 1 ? 's' : ''})
                        </h4>
                    </div>
                    <p className="text-sm text-red-600">
                        Corrija os erros destacados antes de prosseguir.
                    </p>
                </div>
            )}

            {/* Footer Actions */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6">
                <div className="flex flex-col sm:flex-row gap-3">
                    <SecondaryButton
                        onClick={discardChanges}
                        disabled={!isDraft || isLoadingEstrutura}
                        leftIcon={<RefreshCcw className="w-4 h-4" />}
                        aria-label="Descartar mudanças e recarregar dados originais"
                    >
                        Descartar Mudanças
                    </SecondaryButton>

                    <SecondaryButton
                        onClick={handleSaveDraft}
                        disabled={!isDraft || hasValidationErrors || isSavingDraft}
                        isLoading={isSavingDraft}
                        leftIcon={<Save className="w-4 h-4" />}
                        className={!isDraft ? 'opacity-50 cursor-not-allowed' : ''}
                        aria-label="Salvar rascunho do cadastro"
                    >
                        Salvar Rascunho
                    </SecondaryButton>
                </div>

                <PrimaryButton
                    onClick={handleSaveAndNext}
                    disabled={hasValidationErrors || isSaving}
                    isLoading={isSaving}
                    rightIcon={
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    }
                >
                    Salvar Cadastro e Avançar
                </PrimaryButton>
            </div>
        </motion.div>
    );
}; 